import { ENGINE_VERSION, MINIMUM_SDK_AUTO_UPDATE_VERSION, MINIMUM_SDK_VERSION } from '@/const';

export { MINIMUM_SDK_AUTO_UPDATE_VERSION, MINIMUM_SDK_VERSION };

export type SdkCompatibilityMode =
  | 'compatible'
  | 'updateRequiredWaiting'
  | 'manualUpdateRequired'
  | 'compatibilityUnknown';

export type SdkCompatibilityFailureKind =
  | 'sdkVersion'
  | 'engineVersion'
  | 'unknown';

export type RuntimeCompatibilityResult = {
  mode: SdkCompatibilityMode;
  failureKind: SdkCompatibilityFailureKind;
};

const SEMVER_LEADING_PATTERN = /^(\d+)\.(\d+)\.(\d+)/;

/**
 * Normalise a version string to major.minor.0 for major.minor comparisons.
 */
export const toMajorMinorWithZeroPatch = (version: string): string => {
  const parsed = parseVersionMajorMinor(version);
  if (!parsed) {
    return '0.0.0';
  }
  return `${parsed.major}.${parsed.minor}.0`;
};

/**
 * Parse the leading X.Y.Z from a semver string.
 * Returns null for empty or non-semver values (e.g. link:, workspace:*).
 */
export const parseVersionMajorMinor = (version: string): { major: number; minor: number } | null => {
  const trimmed = version?.trim() ?? '';
  if (!trimmed) {
    return null;
  }
  const match = trimmed.match(SEMVER_LEADING_PATTERN);
  if (!match) {
    return null;
  }
  return {
    major: Number(match[1]),
    minor: Number(match[2]),
  };
};

/**
 * Compare two semantic version strings.
 * @param version1 First version string (e.g., '0.22.15')
 * @param version2 Second version string (e.g., '0.22.16')
 * @returns -1 if version1 < version2, 0 if equal, 1 if version1 > version2
 */
export const compareVersions = (version1: string, version2: string): number => {
  const v1Parts = version1.split('.').map(part => Number(part) || 0);
  const v2Parts = version2.split('.').map(part => Number(part) || 0);

  const maxLength = Math.max(v1Parts.length, v2Parts.length);

  for (let i = 0; i < maxLength; i++) {
    const v1Part = v1Parts[i] || 0;
    const v2Part = v2Parts[i] || 0;

    if (v1Part < v2Part) return -1;
    if (v1Part > v2Part) return 1;
  }

  return 0;
};

/**
 * Check if the current SDK version meets the minimum requirement.
 * @param currentVersion Current SDK version string
 * @param minimumVersion Minimum required version string (default: MINIMUM_SDK_VERSION)
 * @returns true if current version is sufficient, false otherwise
 */
export const isVersionSufficient = (currentVersion: string, minimumVersion: string = MINIMUM_SDK_VERSION): boolean => {
  return compareVersions(currentVersion, minimumVersion) >= 0;
};

/**
 * Check if the SDK's bundled engine meets the editor's engine at major.minor.
 * SDK engine ahead of the editor is allowed; only behind is insufficient.
 */
export const isEngineVersionSufficient = (
  sdkEngineVersion: string,
  requiredEngineVersion: string = ENGINE_VERSION
): boolean => {
  const sdkMajorMinor = toMajorMinorWithZeroPatch(sdkEngineVersion);
  const requiredMajorMinor = toMajorMinorWithZeroPatch(requiredEngineVersion);
  if (sdkMajorMinor === '0.0.0' || requiredMajorMinor === '0.0.0') {
    return false;
  }
  return compareVersions(sdkMajorMinor, requiredMajorMinor) >= 0;
};

/**
 * Check if the current SDK version can still use in-app updater recovery.
 * @param currentVersion Current SDK version string
 * @param minimumAutoUpdateVersion Minimum version that supports updater recovery
 * @returns true if updater recovery should be attempted, false for manual-only
 */
export const supportsAutoUpdateRecovery = (
  currentVersion: string,
  minimumAutoUpdateVersion: string = MINIMUM_SDK_AUTO_UPDATE_VERSION
): boolean => {
  return compareVersions(currentVersion, minimumAutoUpdateVersion) >= 0;
};

const resolveInsufficientSdkMode = (currentVersion: string): SdkCompatibilityMode => {
  if (!supportsAutoUpdateRecovery(currentVersion)) {
    return 'manualUpdateRequired';
  }
  return 'updateRequiredWaiting';
};

/**
 * Resolve SDK app-version compatibility mode from version thresholds.
 */
export const getSdkCompatibilityMode = (currentVersion: string): SdkCompatibilityMode => {
  if (!currentVersion || currentVersion.trim().length === 0) {
    return 'compatibilityUnknown';
  }
  if (isVersionSufficient(currentVersion)) {
    return 'compatible';
  }
  return resolveInsufficientSdkMode(currentVersion);
};

/**
 * Resolve engine compatibility mode from the SDK's bundled engine version.
 */
export const getEngineCompatibilityMode = (
  sdkEngineVersion: string | undefined,
  requiredEngineVersion: string = ENGINE_VERSION
): SdkCompatibilityMode => {
  if (!sdkEngineVersion || sdkEngineVersion.trim().length === 0) {
    return 'compatibilityUnknown';
  }
  // special case to deal with local build using workspace dependency
  if (sdkEngineVersion.startsWith('link:')) {
    return 'compatible';
  }
  if (!parseVersionMajorMinor(sdkEngineVersion)) {
    return 'compatibilityUnknown';
  }
  if (isEngineVersionSufficient(sdkEngineVersion, requiredEngineVersion)) {
    return 'compatible';
  }
  return 'updateRequiredWaiting';
};

/**
 * Resolve combined SDK app + engine compatibility for startup gating.
 * SDK app version is checked first, then bundled engine major.minor.
 */
export const getRuntimeCompatibility = (
  appVersion: string,
  sdkEngineVersion: string | undefined,
  requiredEngineVersion: string = ENGINE_VERSION
): RuntimeCompatibilityResult => {
  const sdkMode = getSdkCompatibilityMode(appVersion);
  if (sdkMode !== 'compatible') {
    return {
      mode: sdkMode,
      failureKind: appVersion.trim().length === 0 ? 'unknown' : 'sdkVersion',
    };
  }

  const engineMode = getEngineCompatibilityMode(sdkEngineVersion, requiredEngineVersion);
  if (engineMode === 'compatible') {
    return { mode: 'compatible', failureKind: 'unknown' };
  }
  if (engineMode === 'compatibilityUnknown') {
    return { mode: 'compatibilityUnknown', failureKind: 'unknown' };
  }
  return { mode: engineMode, failureKind: 'engineVersion' };
};
